import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResourceTable } from "@/components/data/ResourceTable";
import { PiecesJointes } from "@/components/data/Network";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, fmtMoney } from "@/lib/api";

const TYPES = { caisse: "Caisse physique", caisse_electronique: "Caisse électronique", mobile_money: "Mobile Money", banque: "Banque", autre: "Autre" };
const TABS = ["comptes", "tresorerie_ops", "virements", "mouvements"];

function Line({ label, value, sign }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{sign}{fmtMoney(value)}</span>
    </div>
  );
}

function CompteCard({ c }) {
  return (
    <div className="p-4 rounded-lg border bg-card space-y-1" data-testid={`compte-card-${c.id}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="text-sm font-bold truncate">{c.nom}</div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{TYPES[c.type] || c.type}{c.frais_transfert_banque ? " · frais vers banque" : ""}</div>
        </div>
      </div>
      <div className={`font-mono text-lg font-bold pb-1 ${c.solde < 0 ? "text-primary" : ""}`} data-testid={`compte-solde-${c.id}`}>{fmtMoney(c.solde)}</div>
      <Line label="Solde initial" value={c.solde_initial || 0} />
      <Line label="Entrées" value={c.entrees} sign="+" />
      <Line label="Sorties" value={c.sorties} sign="−" />
      <Line label="Transferts reçus" value={c.transferts_entrants} sign="+" />
      <Line label="Transferts émis" value={c.transferts_sortants} sign="−" />
    </div>
  );
}

function Comptes({ reload }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/r/comptes", { params: { size: 200 } }).then((r) => setRows(r.data.items.filter((c) => c.statut !== "inactif"))).catch(() => {});
  }, [reload]);
  const total = rows.reduce((s, r) => s + Number(r.solde || 0), 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
      <div className="p-4 rounded-lg bg-[hsl(var(--sidebar))] text-white kgd-grain" data-testid="tresorerie-total">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">Trésorerie totale</div>
        <div className="font-mono text-2xl font-bold mt-1">{fmtMoney(total)}</div>
        <div className="text-[11px] text-white/50 mt-2">{rows.length} compte(s) actif(s)</div>
      </div>
      {rows.map((c) => <CompteCard key={c.id} c={c} />)}
    </div>
  );
}

function FraisDetail({ row }) {
  if (!Number(row.frais)) return null;
  return (
    <div className="mt-6 grid grid-cols-3 gap-2 p-3 rounded-md bg-accent/60 border" data-testid="virement-frais-detail">
      {[["Montant transféré", row.montant], [`Frais ${row.taux_frais || 0} %`, row.frais], ["Net crédité banque", row.montant_net]].map(([l, v]) => (
        <div key={l}><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-mono text-sm font-semibold">{fmtMoney(v)}</div></div>
      ))}
      {row.charge_numero && <div className="col-span-3 text-[11px] text-muted-foreground">Frais enregistrés en charge financière {row.charge_numero}</div>}
    </div>
  );
}

export default function TresoreriePage() {
  const { meta } = useAuth();
  const [reload, setReload] = useState(0);
  const changed = () => setReload((n) => n + 1);
  const extra = {
    tresorerie_ops: (row) => <PiecesJointes res="tresorerie_ops" entityId={row.id} />,
    virements: (row) => <><FraisDetail row={row} /><PiecesJointes res="virements" entityId={row.id} /></>,
  };
  return (
    <div>
      <PageHeader title="Trésorerie & comptes financiers" subtitle="Soldes calculés : solde initial + entrées − sorties + transferts reçus − transferts émis. Seules les opérations validées génèrent des mouvements." />
      <Comptes reload={reload} />
      <Tabs defaultValue="comptes">
        <TabsList className="mb-4 flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}>{meta.resources[t].label}</TabsTrigger>)}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t} value={t}>
            <ResourceTable res={t} reloadKey={reload} onChanged={changed} detailExtra={extra[t]} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
